import {
  GAME_HEIGHT,
  GAME_WIDTH,
  GROUND_Y,
  NET_TOP,
  NET_X,
  SERVE_TICKS,
  type BallState,
  type GameEvent,
  type GameState,
  type PlayerState,
} from './core';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
}

export interface VisualState {
  particles: Particle[];
  shake: number;
}

export function createVisualState(): VisualState {
  return { particles: [], shake: 0 };
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.arcTo(x + width, y, x + width, y + height, safeRadius);
  context.arcTo(x + width, y + height, x, y + height, safeRadius);
  context.arcTo(x, y + height, x, y, safeRadius);
  context.arcTo(x, y, x + width, y, safeRadius);
  context.closePath();
}

function drawCloud(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  scale: number,
): void {
  context.save();
  context.translate(x, y);
  context.scale(scale, scale);
  context.fillStyle = 'rgba(255,255,255,.86)';
  context.beginPath();
  context.arc(-48, 7, 26, Math.PI * 0.5, Math.PI * 1.5);
  context.arc(-20, -12, 34, Math.PI, Math.PI * 1.84);
  context.arc(18, -18, 43, Math.PI * 1.1, Math.PI * 1.9);
  context.arc(54, 5, 28, Math.PI * 1.5, Math.PI * 0.5);
  context.lineTo(-48, 33);
  context.closePath();
  context.fill();
  context.restore();
}

function drawMascot(
  context: CanvasRenderingContext2D,
  player: PlayerState,
  now: number,
): void {
  const bob = player.onGround ? Math.sin(now / 180 + player.side * 2) * 1.8 : 0;
  const stretch = Math.min(0.1, Math.abs(player.vy) / 2_400);
  const bodyColor = player.side === 0 ? '#ff776d' : '#35c8c3';
  const accent = player.side === 0 ? '#c93f53' : '#147f96';
  const bellyColor = player.side === 0 ? '#ffd5c7' : '#c8f4e9';

  context.save();
  context.translate(player.x, player.y + bob);
  context.scale(player.facing, 1);

  context.save();
  context.scale(player.facing, 1);
  context.fillStyle = 'rgba(29,35,46,.18)';
  context.beginPath();
  context.ellipse(0, 48 - bob, player.onGround ? 54 : 32, player.onGround ? 10 : 6, 0, 0, Math.PI * 2);
  context.fill();
  context.restore();

  context.fillStyle = bodyColor;
  context.strokeStyle = '#172338';
  context.lineWidth = 5;
  context.lineJoin = 'round';
  context.beginPath();
  context.moveTo(-14, -37);
  context.bezierCurveTo(-3, -63, 27, -69, 30, -46);
  context.bezierCurveTo(18, -50, 9, -42, 4, -32);
  context.closePath();
  context.fill();
  context.stroke();

  context.fillStyle = bellyColor;
  context.beginPath();
  context.arc(36, -48, 6, 0, Math.PI * 2);
  context.arc(48, -39, 3.5, 0, Math.PI * 2);
  context.fill();

  context.save();
  context.scale(1 - stretch, 1 + stretch);
  context.fillStyle = bodyColor;
  context.strokeStyle = '#172338';
  context.lineWidth = 5;
  context.beginPath();
  context.moveTo(-42, -20);
  context.bezierCurveTo(-57, 3, -47, 38, -19, 47);
  context.bezierCurveTo(9, 56, 46, 42, 49, 10);
  context.bezierCurveTo(53, -22, 25, -41, -8, -39);
  context.bezierCurveTo(-24, -38, -36, -31, -42, -20);
  context.closePath();
  context.fill();
  context.stroke();

  context.fillStyle = '#fff9e9';
  roundedRect(context, -41, -24, 84, 10, 5);
  context.fill();
  context.fillStyle = accent;
  context.beginPath();
  context.arc(1, -19, 4, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = '#172338';
  context.beginPath();
  context.ellipse(-15, -3, 4.5, 6, 0, 0, Math.PI * 2);
  context.ellipse(16, -3, 4.5, 6, 0, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = '#fff';
  context.beginPath();
  context.arc(-16.5, -5, 1.6, 0, Math.PI * 2);
  context.arc(14.5, -5, 1.6, 0, Math.PI * 2);
  context.fill();

  context.strokeStyle = bellyColor;
  context.lineWidth = 7;
  context.lineCap = 'round';
  context.beginPath();
  context.moveTo(-30, 22);
  context.bezierCurveTo(-22, 22, -8, 10, 2, 21);
  context.bezierCurveTo(12, 31, 23, 19, 30, 23);
  context.stroke();

  context.strokeStyle = '#172338';
  context.lineWidth = 2.5;
  context.beginPath();
  context.arc(0, 6, 9, 0.25, Math.PI - 0.25);
  context.stroke();
  context.restore();

  context.fillStyle = bodyColor;
  context.strokeStyle = '#172338';
  context.lineWidth = 4;
  context.beginPath();
  context.ellipse(-22, 40, 19, 10, -0.12, 0, Math.PI * 2);
  context.ellipse(22, 40, 19, 10, 0.12, 0, Math.PI * 2);
  context.fill();
  context.stroke();

  if (player.attack > 0) {
    context.save();
    context.rotate(-0.65);
    context.beginPath();
    context.ellipse(43, -4, 25, 12, 0, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.restore();
  }
  context.restore();
}

function drawBall(context: CanvasRenderingContext2D, ball: BallState): void {
  context.save();
  context.translate(ball.x, ball.y);
  context.rotate(ball.rotation);
  context.shadowColor = 'rgba(23,35,56,.2)';
  context.shadowBlur = 12;
  context.shadowOffsetY = 7;
  context.fillStyle = '#fffdf4';
  context.strokeStyle = '#172338';
  context.lineWidth = 4;
  context.beginPath();
  context.arc(0, 0, ball.radius, 0, Math.PI * 2);
  context.fill();
  context.stroke();
  context.shadowColor = 'transparent';

  context.lineWidth = 6;
  context.lineCap = 'round';
  for (const [rotation, color] of [[0, '#35c8c3'], [2.1, '#ff776d'], [4.2, '#ffd76a']] as const) {
    context.save();
    context.rotate(rotation);
    context.strokeStyle = color;
    context.beginPath();
    context.arc(0, -8, 17, -0.8, 0.8);
    context.stroke();
    context.restore();
  }
  context.restore();
}

function spark(
  visual: VisualState,
  x: number,
  y: number,
  color: string,
  amount: number,
): void {
  for (let index = 0; index < amount; index += 1) {
    const angle = (Math.PI * 2 * index) / amount + Math.random() * 0.4;
    const speed = 90 + Math.random() * 160;
    visual.particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 0.35,
      maxLife: 0.35,
      color,
    });
  }
}

export function applyVisualEvents(visual: VisualState, events: readonly GameEvent[]): void {
  for (const event of events) {
    if (event.type === 'smash') {
      visual.shake = Math.max(visual.shake, 7);
      spark(visual, event.x, event.y, '#ffe34d', 14);
    } else if (event.type === 'hit') {
      spark(visual, event.x, event.y, event.side === 0 ? '#ff776d' : '#35c8c3', 8);
    } else if (event.type === 'point') {
      visual.shake = Math.max(visual.shake, 10);
      spark(visual, event.x, event.y, event.side === 0 ? '#ff776d' : '#147f96', 22);
    }
  }
}

export function updateVisualState(visual: VisualState, deltaSeconds: number): void {
  visual.shake *= Math.pow(0.00012, deltaSeconds);
  for (const particle of visual.particles) {
    particle.life -= deltaSeconds;
    particle.vy += 420 * deltaSeconds;
    particle.x += particle.vx * deltaSeconds;
    particle.y += particle.vy * deltaSeconds;
  }
  visual.particles = visual.particles.filter((particle) => particle.life > 0);
}

export function drawGame(
  context: CanvasRenderingContext2D,
  state: GameState,
  visual: VisualState,
  now: number,
): void {
  context.save();
  if (visual.shake > 0.1) {
    context.translate(
      (Math.random() - 0.5) * visual.shake,
      (Math.random() - 0.5) * visual.shake,
    );
  }

  const sky = context.createLinearGradient(0, 0, 0, 330);
  sky.addColorStop(0, '#62d0f1');
  sky.addColorStop(1, '#c7f4ff');
  context.fillStyle = sky;
  context.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

  context.save();
  context.translate(858, 86);
  context.rotate(now / 9_000);
  context.strokeStyle = 'rgba(255,223,80,.55)';
  context.lineWidth = 8;
  context.lineCap = 'round';
  for (let index = 0; index < 12; index += 1) {
    context.rotate(Math.PI / 6);
    context.beginPath();
    context.moveTo(58, 0);
    context.lineTo(76, 0);
    context.stroke();
  }
  context.restore();
  context.fillStyle = '#ffe46a';
  context.strokeStyle = '#172338';
  context.lineWidth = 4;
  context.beginPath();
  context.arc(858, 86, 43, 0, Math.PI * 2);
  context.fill();
  context.stroke();

  drawCloud(context, 145 + Math.sin(now / 3_500) * 12, 106, 0.78);
  drawCloud(context, 620 + Math.sin(now / 4_200) * 10, 150, 0.48);

  context.fillStyle = '#4cbad7';
  context.fillRect(0, 282, GAME_WIDTH, 88);
  context.fillStyle = '#84d8ce';
  context.beginPath();
  context.moveTo(0, 300);
  context.quadraticCurveTo(85, 230, 180, 299);
  context.quadraticCurveTo(240, 252, 325, 305);
  context.lineTo(325, 350);
  context.lineTo(0, 350);
  context.closePath();
  context.fill();

  for (let x = -20; x < GAME_WIDTH; x += 74) {
    context.beginPath();
    context.arc(x + ((now / 45) % 74), 325, 18, Math.PI, Math.PI * 2);
    context.strokeStyle = 'rgba(255,255,255,.55)';
    context.lineWidth = 3;
    context.stroke();
  }

  const sand = context.createLinearGradient(0, 350, 0, GAME_HEIGHT);
  sand.addColorStop(0, '#f8d879');
  sand.addColorStop(1, '#eeb95b');
  context.fillStyle = sand;
  context.fillRect(0, 350, GAME_WIDTH, GAME_HEIGHT - 350);

  context.strokeStyle = 'rgba(255,255,245,.72)';
  context.lineWidth = 4;
  context.setLineDash([13, 13]);
  context.beginPath();
  context.moveTo(35, GROUND_Y + 1);
  context.lineTo(GAME_WIDTH - 35, GROUND_Y + 1);
  context.stroke();
  context.setLineDash([]);

  context.fillStyle = 'rgba(23,35,56,.15)';
  context.beginPath();
  context.ellipse(NET_X, GROUND_Y + 10, 58, 12, 0, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = '#fffdf4';
  context.strokeStyle = '#172338';
  context.lineWidth = 5;
  roundedRect(context, NET_X - 9, NET_TOP, 18, GROUND_Y - NET_TOP + 4, 6);
  context.fill();
  context.stroke();
  context.strokeStyle = 'rgba(23,35,56,.38)';
  context.lineWidth = 2;
  for (let y = NET_TOP + 22; y < GROUND_Y; y += 22) {
    context.beginPath();
    context.moveTo(NET_X - 8, y);
    context.lineTo(NET_X + 8, y);
    context.stroke();
  }
  context.fillStyle = '#ff776d';
  context.strokeStyle = '#172338';
  context.lineWidth = 4;
  roundedRect(context, NET_X - 38, NET_TOP - 9, 76, 18, 7);
  context.fill();
  context.stroke();
  context.fillStyle = '#fffdf4';
  roundedRect(context, NET_X - 34, GROUND_Y - 2, 68, 17, 8);
  context.fill();
  context.stroke();

  drawMascot(context, state.players[0], now);
  drawMascot(context, state.players[1], now);

  if (state.serveTicks > 0 && state.phase === 'playing') {
    const countdown = Math.max(1, Math.ceil((state.serveTicks / SERVE_TICKS) * 3));
    const pulse = 1 + Math.sin(now / 90) * 0.08;
    context.save();
    context.translate(state.ball.x, state.ball.y);
    context.scale(pulse, pulse);
    context.strokeStyle = 'rgba(255,255,255,.9)';
    context.lineWidth = 4;
    context.setLineDash([7, 8]);
    context.beginPath();
    context.arc(0, 0, state.ball.radius + 13, 0, Math.PI * 2);
    context.stroke();
    context.setLineDash([]);
    context.fillStyle = '#172338';
    context.font = '1000 27px "Noto Sans KR", system-ui, sans-serif';
    context.textAlign = 'center';
    context.fillText(String(countdown), 0, -49);
    context.restore();
  }

  drawBall(context, state.ball);

  for (const particle of visual.particles) {
    context.globalAlpha = Math.max(0, particle.life / particle.maxLife);
    context.fillStyle = particle.color;
    context.beginPath();
    context.arc(
      particle.x,
      particle.y,
      4 + (1 - particle.life / particle.maxLife) * 3,
      0,
      Math.PI * 2,
    );
    context.fill();
  }
  context.globalAlpha = 1;
  context.restore();
}
